import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { ArrowsClockwise } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

export default function Currencies() {
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [rate, setRate] = useState({ date: today() });
  const [refreshing, setRefreshing] = useState(false);

  const refreshRates = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/currencies/refresh-rates");
      toast.success(`Rates updated for ${r.data.updated.length} currencies (${r.data.date})`);
      load();
      if (selected) openHistory(selected);
    } catch (e) { toast.error(errMsg(e) || "Could not fetch rates"); }
    finally { setRefreshing(false); }
  };

  const load = useCallback(() => api.get("/currencies").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openHistory = async (c) => {
    setSelected(c);
    setRate({ currency_code: c.code, date: today(), rate: c.latest_rate || "" });
    const r = await api.get("/forex-rates", { params: { currency: c.code } });
    setHistory(r.data);
  };

  const addCurrency = async () => {
    if (!form.code || !form.name) { toast.error("Code and name are required"); return; }
    try {
      await api.post("/currencies", form);
      toast.success("Currency added");
      setOpen(false); setForm({}); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const delCurrency = async (c) => {
    if (!window.confirm(`Delete currency ${c.code} and all its rates?`)) return;
    try { await api.delete(`/currencies/${c.code}`); toast.success("Currency deleted"); setSelected(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveRate = async () => {
    if (!rate.rate) { toast.error("Enter a rate"); return; }
    try {
      await api.post("/forex-rates", { currency_code: selected.code, date: rate.date, rate: Number(rate.rate) });
      toast.success(`Rate saved for ${selected.code} on ${rate.date}`);
      openHistory(selected); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Layout>
      <PageHeader
        title="Currency Manager"
        subtitle="Base currency TZS · daily forex rates (1 unit foreign = X TZS)"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="refresh-rates-btn" disabled={refreshing} onClick={refreshRates}>
              <ArrowsClockwise size={16} className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />{refreshing ? "Fetching…" : "Refresh rates now"}
            </Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-currency-btn" onClick={() => { setForm({}); setOpen(true); }}><Plus size={16} className="mr-1" />Add Currency</Button>
          </div>
        }
      />
      <div className="grid grid-cols-3 gap-6 p-8">
        <div className="col-span-1 overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Code</th><th className="px-4 py-2 text-right">Latest Rate</th><th className="px-4 py-2"></th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.code} data-testid={`currency-row-${c.code}`} className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${selected?.code === c.code ? "bg-orange-50" : ""}`} onClick={() => openHistory(c)}>
                  <td className="px-4 py-2 font-medium">{c.code} {c.is_base && <span className="ml-1 rounded-sm bg-gray-900 px-1.5 py-0.5 text-[10px] text-white">BASE</span>}<div className="text-xs text-gray-400">{c.name}</div></td>
                  <td className="px-4 py-2 text-right lx-num">{c.is_base ? "1.00" : (c.latest_rate ? fmtNum(c.latest_rate) : "—")}</td>
                  <td className="px-4 py-2 text-right">{!c.is_base && <button className="text-gray-300 hover:text-red-500" data-testid={`delete-currency-${c.code}`} onClick={(e) => { e.stopPropagation(); delCurrency(c); }}><Trash size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="col-span-2">
          {!selected ? (
            <div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">Select a currency to view and enter daily rates.</div>
          ) : selected.is_base ? (
            <div className="rounded-sm border border-gray-200 bg-white p-6 text-sm text-gray-500">{selected.code} is the base currency and always equals 1.00.</div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-sm border border-gray-200 bg-white p-4">
                <div className="lx-label mb-2">Enter Daily Rate — {selected.code}</div>
                <div className="flex items-end gap-3">
                  <div><Label className="lx-label">Date</Label><DateField className="mt-1" data-testid="rate-date" value={rate.date} onChange={(e) => setRate({ ...rate, date: e.target.value })} /></div>
                  <div><Label className="lx-label">1 {selected.code} = ? TZS</Label><Input type="number" className="mt-1" data-testid="rate-value" value={rate.rate || ""} onChange={(e) => setRate({ ...rate, rate: e.target.value })} /></div>
                  <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-rate" onClick={saveRate}>Save Rate</Button>
                </div>
              </div>
              <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200"><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right">Rate (TZS)</th><th className="px-4 py-2 text-left">Entered By</th></tr></thead>
                  <tbody>
                    {history.length === 0 && <TableEmpty colSpan={3} title="No rates yet" message="Rate history for this currency will appear here." />}
                    {history.map((h) => (
                      <tr key={h.date} className="border-b border-gray-100"><td className="px-4 py-2 lx-num">{fmtDate(h.date)}</td><td className="px-4 py-2 text-right lx-num">{fmtNum(h.rate)}</td><td className="px-4 py-2">{h.entered_by}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="currency-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Currency</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="lx-label">Code (e.g. USD)</Label><Input className="mt-1" data-testid="currency-code" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></div>
            <div><Label className="lx-label">Symbol</Label><Input className="mt-1" data-testid="currency-symbol" value={form.symbol || ""} onChange={(e) => setForm({ ...form, symbol: e.target.value })} /></div>
            <div className="col-span-2"><Label className="lx-label">Name</Label><Input className="mt-1" data-testid="currency-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-currency" onClick={addCurrency}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
